// EGY CSEMPE A SZERKESZTŐ-MÓDBAN: összecsukott előnézet (ikon + cím + egysoros leírás) + vezérlők.
//   • húzó-fogantyú (egér/érintés/billentyű — a @dnd-kit szenzorai a szerkesztőben),
//   • fel/le gomb: a második, húzás nélküli út (MOBILON ez az elsődleges),
//   • méret-kapcsoló (Közepes / Széles; a KPI fix „Kicsi”) — csak ahol a katalógus több méretet enged,
//   • eltávolítás (megerősítés nélkül: a Mégse mindent visszavon).
// Az előnézet szándékosan NEM a valódi tartalom: a magas agenda nem nyújtja a célterületet, a húzás kiszámítható marad.
import { type DashboardTileEntry, type DashboardTileSize, dashboardTileDef } from '@atlas/shared';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { Bars2Icon, LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Segmented } from '@/components/ui/segmented';
import { tileSpanClass, tileSpanKind } from '@/features/dashboard/layout/spans';
import { tileIcon } from '@/features/dashboard/tiles/tile-icon';
import { cn } from '@/lib/utils';

export interface TileEditFrameProps {
  entry: DashboardTileEntry;
  index: number;
  count: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onResize: (size: DashboardTileSize) => void;
}

export function TileEditFrame({
  entry,
  index,
  count,
  onMoveUp,
  onMoveDown,
  onRemove,
  onResize,
}: TileEditFrameProps): JSX.Element | null {
  const { t } = useTranslation();
  const def = dashboardTileDef(entry.id);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });
  if (!def) return null;
  const Icon = tileIcon(def);
  const title = t(def.titleKey);
  const canResize = def.allowedSizes.length > 1;
  const kind = tileSpanKind({ def, size: entry.size });

  // a KICSI (KPI) keretben a gombsor alulra kerül: a negyed-cellában a cím mellett nem fért el (a cím „Ügyf…"-re
  // vágódott, majd szó közben tört)
  const controls = (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={index === 0}
        onClick={onMoveUp}
        aria-label={t('dashboard.editor.moveUp', { tile: title })}
      >
        <ChevronUpIcon />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={index === count - 1}
        onClick={onMoveDown}
        aria-label={t('dashboard.editor.moveDown', { tile: title })}
      >
        <ChevronDownIcon />
      </Button>
      {def.removable ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={onRemove}
          aria-label={t('dashboard.editor.remove', { tile: title })}
        >
          <XMarkIcon />
        </Button>
      ) : (
        <span
          data-testid={`tile-mandatory-${entry.id}`}
          title={t('dashboard.editor.mandatoryHint')}
          className="text-muted-foreground inline-flex size-9 items-center justify-center"
        >
          <LockClosedIcon className="size-4" aria-hidden />
          <span className="sr-only">{t('dashboard.editor.mandatoryHint')}</span>
        </span>
      )}
    </div>
  );

  return (
    <li
      ref={setNodeRef}
      id={`tile-frame-${entry.id}`}
      data-span={kind}
      data-testid={`tile-frame-${entry.id}`}
      // a @dnd-kit a húzás közbeni mozgatást CSAK `style`-ként tudja átadni (a könyvtár API-ja) — a CSS.Transform
      // string dinamikus érték, nem statikus stílus
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'card-glass flex min-h-24 min-w-0 flex-col gap-2 rounded-xl border p-3',
        tileSpanClass(kind),
        isDragging && 'ring-ring relative z-10 opacity-90 shadow-lg ring-2',
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          ref={setActivatorNodeRef}
          data-tile-handle={entry.id}
          {...attributes}
          {...listeners}
          aria-roledescription={t('dashboard.editor.draggable')}
          aria-label={t('dashboard.editor.dragHandle', { tile: title })}
          className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -ml-1 inline-flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 active:cursor-grabbing"
        >
          <Bars2Icon className="size-5" aria-hidden />
        </button>
        <Icon className="text-muted-foreground size-5 shrink-0" aria-hidden />
        <div className="text-md text-foreground line-clamp-2 min-w-0 flex-1 font-semibold">
          {title}
        </div>
        {kind !== 'small' && controls}
      </div>
      <div className="text-muted-foreground line-clamp-2 min-w-0 text-sm">
        {t(def.descriptionKey)}
      </div>
      <div className="mt-auto flex items-center justify-between gap-2">
        {canResize ? (
          <Segmented
            aria-label={t('dashboard.editor.sizeLabel', { tile: title })}
            value={String(entry.size)}
            onChange={(v) => onResize(v === '2' ? 2 : 1)}
            options={[
              { value: '1', label: t('dashboard.editor.sizeMedium') },
              { value: '2', label: t('dashboard.editor.sizeWide') },
            ]}
          />
        ) : (
          // a KPI mindig kicsi: nincs kapcsoló, csak a rögzített méret jelzése
          <span
            data-testid={`tile-size-fixed-${entry.id}`}
            className="bg-muted text-muted-foreground inline-flex rounded-md px-2 py-0.5 text-xs font-medium"
          >
            {t('dashboard.editor.sizeSmall')}
          </span>
        )}
        {kind === 'small' && controls}
      </div>
    </li>
  );
}
